import { useEffect, useState, Fragment } from 'react';
import { reportApi } from '../utils/api';
import { Building2, ChevronDown, Loader, Download, Palmtree } from 'lucide-react';
import { Button } from './ui/button';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const fmtDays = (n) => {
  const v = Math.round((n || 0) * 10) / 10;
  return v % 1 === 0 ? `${v}` : v.toFixed(1);
};

export const AdminVacationByDepartment = () => {
  const [data, setData] = useState({ departments: [] });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    reportApi.vacationByDepartment()
      .then((res) => setData(res.data))
      .catch((e) => { console.error('vacation-by-department error:', e); setData({ departments: [] }); })
      .finally(() => setLoading(false));
  }, []);

  const toggle = (dept) => setExpanded((prev) => (prev === dept ? null : dept));

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await reportApi.exportVacationByDepartment();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vacaciones_pendientes_por_departamento_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    finally { setExporting(false); }
  };

  const chartData = data.departments.slice(0, 10).map((d) => ({
    name: d.department.length > 18 ? `${d.department.slice(0, 18)}…` : d.department,
    fullName: d.department,
    dias: d.total_available_days,
  }));

  return (
    <div className="bg-card border border-border rounded-xl p-5 shadow-sm" data-testid="admin-vacation-by-department">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold font-heading text-sm flex items-center gap-2">
          <Building2 className="h-4 w-4 text-brand" />
          Vacaciones pendientes por departamento
          <span className="text-xs font-normal text-muted-foreground">(Centro de Coste · {data.year})</span>
        </h2>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={exporting || loading}
          data-testid="btn-export-vacation-by-department"
          className="gap-2 text-xs"
        >
          {exporting ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
          Exportar Excel
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader className="h-5 w-5 animate-spin text-brand" />
        </div>
      ) : data.departments.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-6">Sin datos de saldos disponibles.</p>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} interval={0} angle={-20} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                formatter={(value) => [`${fmtDays(value)} días`, 'Pendientes']}
                labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ''}
              />
              <Bar dataKey="dias" fill="#00a651" radius={[3, 3, 0, 0]} name="Días pendientes" />
            </BarChart>
          </ResponsiveContainer>

          <div className="overflow-x-auto mt-4">
            <table className="w-full text-xs" data-testid="vacation-by-department-table">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-2 pr-2 font-medium">Centro de Coste</th>
                  <th className="py-2 pr-2 font-medium">Empleados</th>
                  <th className="py-2 pr-2 font-medium">Días pendientes (total)</th>
                  <th className="py-2 pr-2 font-medium">Media/empleado</th>
                  <th className="py-2 pr-2 font-medium w-6" />
                </tr>
              </thead>
              <tbody>
                {data.departments.map((dept) => {
                  const isOpen = expanded === dept.department;
                  return (
                    <Fragment key={dept.department}>
                      <tr
                        onClick={() => toggle(dept.department)}
                        data-testid={`dept-row-${dept.department}`}
                        className="border-b border-border/60 last:border-0 cursor-pointer hover:bg-muted/40 transition-colors"
                      >
                        <td className="py-2 pr-2 font-medium truncate max-w-[220px]">{dept.department}</td>
                        <td className="py-2 pr-2">{dept.employee_count}</td>
                        <td className="py-2 pr-2">
                          <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400 font-semibold">
                            <Palmtree className="h-3 w-3" />
                            {fmtDays(dept.total_available_days)}d
                          </span>
                        </td>
                        <td className="py-2 pr-2">{fmtDays(dept.avg_available_days)}d</td>
                        <td className="py-2 pr-2 text-right">
                          <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={5} className="pb-3">
                            <div className="bg-muted/30 rounded-lg p-2.5 space-y-1.5 max-h-56 overflow-y-auto" data-testid={`dept-expand-${dept.department}`}>
                              {dept.employees.map((emp) => (
                                <div key={emp.user_id} className="flex items-center justify-between text-xs">
                                  <span className="truncate">{emp.user_name}</span>
                                  <span className="text-muted-foreground flex-shrink-0 ml-2">
                                    {fmtDays(emp.vacation_available)}d disp. de {fmtDays(emp.vacation_total)}d
                                  </span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
